// Centralized layer manager (S15). The extension point for future gameplay
// systems (natural resources, agriculture, logistics, supply, military ops,
// economy) so they integrate without touching MapView's core.
//
// A feature module registers a *layer definition* describing a GeoJSON source +
// one MapLibre layer and how to derive its data / visibility from an app-context
// object. `useExtensionLayers` (see useExtensionLayers.jsx) walks the registry,
// creates each layer once, and keeps its data + visibility in sync — exactly the
// pattern the built-in hooks (useStructures, useBubbleMap, …) already follow, but
// declarative so a new system is a few lines, not a new hook wired into MapView.
//
// Registered layers are inert until a module calls registerFeatureLayer, so this
// ships as a no-op scaffold today.

const registry = new Map()

/**
 * @param {object} def
 * @param {string} def.id       Unique layer id (also the source id).
 * @param {string} def.system   Owning system, e.g. 'resources' | 'logistics'.
 * @param {(ctx) => GeoJSON} def.data       Builds the source FeatureCollection.
 * @param {(ctx) => boolean}  def.visible    Whether the layer shows.
 * @param {object} def.layer    MapLibre layer spec (id/type/paint/layout); source injected.
 * @param {string} [def.beforeId] Insert-before layer id (z-order).
 */
export function registerFeatureLayer(def) {
  if (!def?.id) throw new Error('registerFeatureLayer: def.id required')
  registry.set(def.id, def)
}

export const unregisterFeatureLayer = (id) => registry.delete(id)
export const getFeatureLayers = () => [...registry.values()]

// Canonical ids of the built-in map layers — a single source of truth to avoid
// magic strings and to document the render stack for anyone adding a layer.
export const MAP_LAYERS = Object.freeze({
  BACKGROUND: 'background',
  SATELLITE: 'satellite',
  TOPOGRAPHY: 'topography',
  REGIONS_FILL: 'regions-fill',
  REGIONS_BORDER: 'regions-border',
  COUNTRY_BORDER: 'country-border',
  COUNTRY_HALO: 'country-halo',
  LEGEND_DIM: 'legend-dim',
  MULTISELECT_FILL: 'regions-multiselect-fill',
  MULTISELECT_LINE: 'regions-multiselect-line',
  REGIONS_HOVER: 'regions-hover',
  BUBBLE_MAP: 'bubble-map',
  STRUCTURES: 'structures-points',
  INFRASTRUCTURE: 'infrastructure-lines',
  WORLD_ROADS: 'world-roads',
  WORLD_RAILWAYS: 'world-railways',
  WORLD_CITIES: 'world-cities-dots',
  WEATHER_SYSTEMS: 'weather-systems-fill',
})

// Systems planned to plug in here (no data/layers yet — see the spec's
// "future extensibility" list). Kept as documentation + a stable enum.
export const FUTURE_SYSTEMS = Object.freeze([
  'resources', 'agriculture', 'logistics', 'supply', 'military-ops', 'economy',
])
